// Meta Pixel helpers for catalog (dynamic) ads.
// The base Pixel is loaded in public/index.html. Every event that refers to a
// car sends content_ids = [lot_id] with content_type "product" so Meta can
// match the visitor to the same item in the Bid Caribbean catalog feed
// (served by the backend at /api/v1/meta/catalog-feed.csv).

const CURRENCY = "USD";

const fbqReady = () =>
  typeof window !== "undefined" && typeof window.fbq === "function";

const newEventId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const trackMeta = (event, params = {}, { custom = false } = {}) => {
  if (!fbqReady()) return null;
  const eventID = newEventId();
  try {
    window.fbq(custom ? "trackCustom" : "track", event, params, { eventID });
  } catch (e) {
    // Never let tracking break the page.
  }
  return eventID;
};

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

// Price to report for a car: Buy Now price if it has one, else the current bid.
export const carPrice = (car = {}) =>
  toNumber(car.price_new) ??
  toNumber(car.buy_now_price) ??
  toNumber(Math.max(Number(car.current_bid) || 0, Number(car.currentBid) || 0));

const carParams = (lotId, car = {}, extra = {}) => {
  const value = extra.value ?? carPrice(car);
  const params = {
    content_ids: [String(lotId)],
    content_type: "product",
    content_name: car.title,
    content_category: car.vehicle_type,
    ...extra,
  };
  if (value) {
    params.value = value;
    params.currency = CURRENCY;
  } else {
    delete params.value;
  }
  return params;
};

// Fired once per vehicle detail page view.
export const trackViewVehicle = (lotId, car) =>
  trackMeta("ViewContent", carParams(lotId, car));

// Fired when a search/results page loads (top results help Meta retarget).
export const trackSearch = (searchString, cars = []) => {
  const ids = cars
    .slice(0, 10)
    .map((c) => c?.lot_id)
    .filter(Boolean)
    .map(String);
  return trackMeta("Search", {
    search_string: searchString || undefined,
    content_ids: ids.length ? ids : undefined,
    content_type: "product",
  });
};

// Fired when a signed-in user saves a car.
export const trackSaveVehicle = (lotId, car) =>
  trackMeta("AddToWishlist", carParams(lotId, car));

// Fired when a bid or Buy Now is placed successfully.
export const trackBid = (lotId, car, amount, { buyNow = false } = {}) =>
  trackMeta(
    "InitiateCheckout",
    carParams(lotId, car, {
      value: toNumber(amount),
      num_items: 1,
      bid_type: buyNow ? "buy_now" : "bid",
    })
  );

// Fired after a successful account sign-up.
export const trackSignup = () =>
  trackMeta("CompleteRegistration", { status: true });

// The app is a single-page app, so route changes need their own PageView
// (index.html only fires the first one).
export const trackPageView = () => trackMeta("PageView");
